/**
 * "context" is where a document lives when the reader came from somewhere else, e.g. its
 * workspace when it was opened from the Workspaces search (LAR-235). It shows in the
 * breadcrumb as a way there, but "Tilbage" skips it and returns to where the reader was.
 */
export type NavigationStackFrameKind =
  "root" | "datasource" | "context" | "document";

export type NavigationStackFrame = {
  label: string;
  url: string;
  frameKind: NavigationStackFrameKind;
  documentKey?: string;
  /** Used to reattach datasource filter onClick in legislation HTML views. */
  datasourceId?: string;
};

export const DOCUMENT_NAVIGATION_STACK_STORAGE_KEY = "documentNavigationStack";

export const MAX_STACK_FRAMES = 10;

const stackListeners = new Set<() => void>();

export const subscribeNavigationStack = (
  listener: () => void
): (() => void) => {
  stackListeners.add(listener);
  return () => {
    stackListeners.delete(listener);
  };
};

const notifyNavigationStackChanged = (): void => {
  stackListeners.forEach((listener) => listener());
};

const readStackFromStorage = (): NavigationStackFrame[] => {
  if (typeof sessionStorage === "undefined") {
    return [];
  }
  const stored = sessionStorage.getItem(DOCUMENT_NAVIGATION_STACK_STORAGE_KEY);
  if (!stored) {
    return [];
  }
  try {
    const parsed = JSON.parse(stored) as NavigationStackFrame[];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(
      (frame) =>
        typeof frame.label === "string" &&
        typeof frame.url === "string" &&
        (frame.frameKind === "root" ||
          frame.frameKind === "datasource" ||
          frame.frameKind === "context" ||
          frame.frameKind === "document")
    );
  } catch {
    return [];
  }
};

const capStackFrames = (
  frames: NavigationStackFrame[]
): NavigationStackFrame[] => {
  if (frames.length <= MAX_STACK_FRAMES) {
    return frames;
  }
  if (frames[0]?.frameKind === "root" && frames[1]?.frameKind === "context") {
    return [frames[0], frames[1], ...frames.slice(-(MAX_STACK_FRAMES - 2))];
  }
  if (frames[0]?.frameKind === "root") {
    return [frames[0], ...frames.slice(-(MAX_STACK_FRAMES - 1))];
  }
  return frames.slice(-MAX_STACK_FRAMES);
};

const writeStackToStorage = (frames: NavigationStackFrame[]): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  if (frames.length === 0) {
    sessionStorage.removeItem(DOCUMENT_NAVIGATION_STACK_STORAGE_KEY);
    notifyNavigationStackChanged();
    return;
  }
  sessionStorage.setItem(
    DOCUMENT_NAVIGATION_STACK_STORAGE_KEY,
    JSON.stringify(capStackFrames(frames))
  );
  notifyNavigationStackChanged();
};

export const getStack = (): NavigationStackFrame[] => readStackFromStorage();

export const setStack = (frames: NavigationStackFrame[]): void => {
  writeStackToStorage(frames);
};

export const clearStack = (): void => {
  writeStackToStorage([]);
};

export const seedStack = (frames: NavigationStackFrame[]): void => {
  writeStackToStorage(frames);
};

export const pushFrame = (frame: NavigationStackFrame): void => {
  const stack = readStackFromStorage();
  const last = stack[stack.length - 1];
  if (last?.documentKey && last.documentKey === frame.documentKey) {
    return;
  }
  stack.push(frame);
  writeStackToStorage(stack);
};

export const replaceTopFrame = (frame: NavigationStackFrame): void => {
  const stack = readStackFromStorage();
  if (stack.length === 0) {
    writeStackToStorage([frame]);
    return;
  }
  stack[stack.length - 1] = frame;
  writeStackToStorage(stack);
};

export const updateTopFrame = (
  patch: Partial<Pick<NavigationStackFrame, "label" | "url" | "documentKey">>
): void => {
  const stack = readStackFromStorage();
  if (stack.length === 0) {
    return;
  }
  stack[stack.length - 1] = { ...stack[stack.length - 1], ...patch };
  writeStackToStorage(stack);
};

export const updateRootFrame = (
  patch: Partial<Pick<NavigationStackFrame, "label" | "url">>
): void => {
  const stack = readStackFromStorage();
  if (stack.length === 0 || stack[0].frameKind !== "root") {
    return;
  }
  stack[0] = { ...stack[0], ...patch };
  writeStackToStorage(stack);
};

export const popFrame = (): NavigationStackFrame | undefined => {
  const stack = readStackFromStorage();
  if (stack.length === 0) {
    return undefined;
  }
  const popped = stack.pop();
  writeStackToStorage(stack);
  return popped;
};

/**
 * "Tilbage": drops the current frame and any context frames under it, and returns the
 * frame the reader goes back to.
 */
export const popToPreviousHistoryFrame = ():
  NavigationStackFrame | undefined => {
  const stack = readStackFromStorage();
  if (stack.length === 0) {
    return undefined;
  }
  stack.pop();
  while (stack.length > 1 && stack[stack.length - 1].frameKind === "context") {
    stack.pop();
  }
  writeStackToStorage(stack);
  return stack[stack.length - 1];
};

export const truncateStackTo = (index: number): void => {
  const stack = readStackFromStorage();
  if (index < 0) {
    clearStack();
    return;
  }
  writeStackToStorage(stack.slice(0, index + 1));
};

export const peekStackBackUrl = (): string | undefined => {
  const stack = readStackFromStorage();
  if (stack.length < 2) {
    return undefined;
  }
  return stack[stack.length - 2]?.url;
};

export const peekCurrentFrameUrl = (): string | undefined => {
  const stack = readStackFromStorage();
  return stack[stack.length - 1]?.url;
};

export const ensureDatasourceFrame = (frame: NavigationStackFrame): void => {
  const stack = readStackFromStorage();
  if (stack.length === 0 || stack[0].frameKind !== "root") {
    return;
  }
  const existing = stack[1];
  if (existing?.frameKind === "datasource") {
    if (existing.datasourceId === frame.datasourceId) {
      return;
    }
    stack[1] = frame;
    writeStackToStorage(stack);
    return;
  }
  stack.splice(1, 0, frame);
  writeStackToStorage(stack);
};

/** Puts `frame` right under the root, replacing a context frame that is already there. */
export const ensureContextFrame = (frame: NavigationStackFrame): void => {
  const stack = readStackFromStorage();
  if (stack.length === 0 || stack[0].frameKind !== "root") {
    return;
  }
  if (stack[1]?.frameKind === "context") {
    if (stack[1].label === frame.label && stack[1].url === frame.url) {
      return;
    }
    stack[1] = frame;
  } else {
    stack.splice(1, 0, frame);
  }
  writeStackToStorage(stack);
};

export const getStackEntryRootKind = ():
  "kilder" | "workspace" | "chat" | null => {
  const root = readStackFromStorage()[0];
  if (!root || root.frameKind !== "root") {
    return null;
  }
  if (root.url === "/chat" || root.url.startsWith("/chat/")) {
    return "chat";
  }
  if (root.url.includes("/workspaces/") && root.url.includes("/files")) {
    return "workspace";
  }
  if (root.url.includes("/legislation")) {
    return "kilder";
  }
  return null;
};
